import React, { useState, useMemo, useCallback } from 'react';
import { Button, ScrollView, useTheme } from 'tamagui';

import { CobuContainer, CobuStack, CobuText } from '@cobu/ui';
import { ResultCard } from './ResultCard';
import { ReferralStats } from './ReferralStats';
import { CustomizeSection } from './CustomizeSection';
import { ArrowLeft } from '@tamagui/lucide-icons';
import { Strings } from '../../constants/Strings';
import { CpuSettingsIcon, RankingIcon } from '../../assets/icons/ReferralIcons';

enum ResultSubContent {
  stats = 'stats',
  customize = 'customize',
}

const yourResultsStrings = Strings.get().referral.yourResults;

const resultHeadings = {
  [ResultSubContent.stats]: yourResultsStrings.sections.stats.heading,
  [ResultSubContent.customize]: yourResultsStrings.sections.customize.heading,
};

export const YourResults: React.FC = () => {
  const [subContent, setSubContent] = useState<ResultSubContent | null>(null);
  const theme = useTheme();

  const onStats = useCallback(() => {
    setSubContent(ResultSubContent.stats);
  }, []);

  const onCustomize = useCallback(() => {
    setSubContent(ResultSubContent.customize);
  }, []);

  const onBack = useCallback(() => {
    setSubContent(null);
  }, []);

  const showBackButton = !!subContent;

  const content = useMemo(() => {
    switch (subContent) {
      case ResultSubContent.customize:
        return <CustomizeSection />;
      case ResultSubContent.stats:
        return <ReferralStats />;
      default:
        return (
          <ScrollView showsVerticalScrollIndicator={false}>
            <CobuStack p="$4" gap="$4">
              <ResultCard
                heading={yourResultsStrings.sections.customize.title}
                subHeading={yourResultsStrings.sections.customize.subtitle}
                gradientColors={[
                  "$referralCustomizePrimary",
                  "$referralCustomizeSecondary",
                ]}
                onPress={onCustomize}
                iconComponent={<CpuSettingsIcon color={theme?.white?.val || '#ffffff'} />}
              />

              <ResultCard
                heading={yourResultsStrings.sections.stats.title}
                subHeading={yourResultsStrings.sections.stats.subtitle}
                gradientColors={[
                  "$referralStatsPrimary",
                  "$referralStatsSecondary",
                ]}
                onPress={onStats}
                iconComponent={<RankingIcon color={theme?.white?.val || '#ffffff'} />}
              />
            </CobuStack>
          </ScrollView>
        );
    }
  }, [subContent, onCustomize, onStats]);

  const heading = subContent ? resultHeadings[subContent] : yourResultsStrings.title;

  return (
    <CobuContainer f={1} bg="$white">
      <CobuStack layout="horizontal" ai="center" px="$4" py="$2" bbw={1} bbc="$backgroundMuted" bg="$white">
        {showBackButton && (
          <Button
            onPress={onBack}
            backgroundColor="$white"
            borderWidth={0}
            height={24}
            padding={0}
            icon={<ArrowLeft size={24} color="$cobuDarkBlue" />}
          />
        )}
        <CobuText variant="heading" fontSize="$5" fontWeight="bold" color="$cobuDarkBlue" f={1}>
          {heading}
        </CobuText>
      </CobuStack>
      {content}
    </CobuContainer>
  );
};
